/**
 * MentionChip -- inline styled span for a mentioned entity
 * Renders inside the overlay layer of RichTextarea
 */

import { X, FileText, Tag, User, Shield } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { MentionEntity, MentionEntityType } from '@/types/rich-input'
import { ENTITY_TYPE_LABELS } from '@/types/rich-input'

const ICON_MAP: Record<MentionEntityType, React.ElementType> = {
  policy: FileText,
  attribute: Tag,
  subject: User,
  classification: Shield,
}

interface MentionChipProps {
  readonly entity: MentionEntity
  readonly onRemove?: () => void
}

export function MentionChip({ entity, onRemove }: MentionChipProps) {
  const Icon = ICON_MAP[entity.entityType]

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary px-1.5 py-0.5 text-sm font-medium whitespace-nowrap">
            <Icon className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[150px]">
              {entity.displayName}
            </span>
            {onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove()
                }}
                className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded hover:bg-primary/20 transition-colors"
                aria-label={`Remove ${entity.displayName}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-medium">
            {ENTITY_TYPE_LABELS[entity.entityType]}: {entity.displayName}
          </p>
          {entity.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {entity.description}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
